import { isApiErrorCode } from "@ckgo2/shared";
import { ApiError } from "@/api/client";
import i18n from "@/i18n";
import { apiErrorText } from "@/lib/apiErrorText";

// Only override where camelCase → Title Case produces a confusing label.
// Auto-conversion handles the rest (e.g., "shortDescription" → "Short Description").
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  destinationId: "Destination",
  guideId: "Guide",
  parentId: "Parent destination",
  tripId: "Trip",
  priceFrom: "Price",
  priceOriginal: "Original price",
  priceEur: "Price (EUR)",
  durationDays: "Duration (days)",
  groupSizeMin: "Min. group size",
  groupSizeMax: "Max. group size",
  // Blok „Video destinace" (vize F5.4) — stejná jména jako popisky editoru.
  heroVideoUrl: "Video URL",
  moreVideosUrl: "Link to more videos",
  videoPromo: "Destination video",
  guideVideoUrl: "Guide video URL",
  metaTitle: "SEO title",
  metaDescription: "SEO description",
  yearsExperience: "Experience (years)",
  readTime: "Read time (min)",
  isFeatured: "Featured",
  isLastMinute: "Last minute",
  isForWomen: "For women",
  groupSizeOpenEnded: "No upper group-size limit",
  isActive: "Active",
  hasCzechGuide: "Czech guide",
  allowsOnlineBooking: "Online booking",
  hotelCoordinatesText: "Hotel coordinates",
  // Paths that carry registry codes (vize F13.9) — camelToTitle would read "Hotel Longitude".
  hotelLatitude: "Hotel latitude",
  hotelLongitude: "Hotel longitude",
  dates: "Departures",
  capacity: "Capacity",
  // Profil průvodce (vize F6.4): camelToTitle by dal „Country Ids“ a „Gallery Image Ids“.
  countryIds: "Countries",
  galleryImageIds: "Gallery",
  favoritePlace: "Favourite place or experience",
  food: "What to taste",
  advice: "Advice before the trip",
};

// Czech overrides for the same fields. The camelCase → Title Case fallback stays
// English-ish for unmapped fields — field names themselves are English.
const FIELD_LABEL_OVERRIDES_CS: Record<string, string> = {
  destinationId: "Destinace",
  guideId: "Průvodce",
  parentId: "Nadřazená destinace",
  tripId: "Zájezd",
  priceFrom: "Cena",
  priceOriginal: "Původní cena",
  priceEur: "Cena (EUR)",
  durationDays: "Délka (dny)",
  groupSizeMin: "Min. velikost skupiny",
  groupSizeMax: "Max. velikost skupiny",
  // Blok „Video destinace" (vize F5.4) — bez nich by banner hlásil „More Videos Url".
  heroVideoUrl: "Adresa videa",
  moreVideosUrl: "Odkaz na další videa",
  videoPromo: "Video destinace",
  guideVideoUrl: "URL videa průvodce",
  metaTitle: "SEO titulek",
  metaDescription: "SEO popis",
  yearsExperience: "Praxe (roky)",
  readTime: "Doba čtení (min)",
  isFeatured: "Doporučené",
  isLastMinute: "Last minute",
  isForWomen: "Pro ženy",
  groupSizeOpenEnded: "Bez horní hranice skupiny",
  isActive: "Aktivní",
  hasCzechGuide: "Český průvodce",
  allowsOnlineBooking: "Online rezervace",
  // Obsahová pole. Bez nich hlásil banner „Activity Type" u pole, které je v editoru
  // popsané jako „Typ aktivity" — redakce ho podle chybové hlášky nenašla.
  title: "Titulek",
  slug: "URL slug",
  status: "Stav",
  activityType: "Typ aktivity",
  difficultyLevel: "Náročnost",
  duration: "Délka",
  location: "Lokalita",
  shortDescription: "Krátký popis",
  description: "Popis",
  excerpt: "Perex",
  content: "Obsah",
  category: "Kategorie",
  heroTitle: "Titulek na hero obrázku",
  heading: "Hlavní nadpis (H1)",
  tips: "Tipy",
  tipsContent: "Tipy (text)",
  videos: "Videa",
  socialReels: "Reels",
  included: "V ceně",
  tags: "Štítky",
  relatedItems: "Související",
  relatedPageIds: "Související stránky",
  relatedTripIds: "Související zájezdy",
  galleryImageIds: "Galerie",
  featuredImageId: "Hlavní obrázek",
  authorName: "Autor",
  testimonialQuote: "Citace",
  testimonialAuthor: "Autor citace",
  sortOrder: "Pořadí",
  publishedAt: "Datum publikace",
  region: "Region",
  continent: "Kontinent",
  tripType: "Typ zájezdu",
  tripKind: "Druh zájezdu",
  tripStyle: "Styl zájezdu",
  // Segmenty vnořených cest — bez nich by stopa zněla „Tipy 1. → Items 3. → Answer".
  items: "položky",
  question: "Otázka",
  answer: "Odpověď",
  topic: "Kategorie",
  itinerary: "Itinerář",
  faqs: "Časté dotazy",
  highlights: "Highlighty",
  regions: "Regiony",
  hotelRooms: "Typy pokojů",
  hotelMealPlans: "Stravování",
  hotelDepartures: "Termíny odletu",
  hotelFeatures: "Vybavení hotelu",
  hotelCoordinatesText: "Souřadnice hotelu",
  // Cesty, na kterých chodí kódy z registru (vize F13.9) — jinak by banner zněl
  // „Hotel Longitude: <česky>".
  hotelLatitude: "Zeměpisná šířka hotelu",
  hotelLongitude: "Zeměpisná délka hotelu",
  dates: "Termíny",
  capacity: "Kapacita",
  optionalActivities: "Volitelné aktivity",
  url: "Odkaz",
  href: "Odkaz",
  thumbnail: "Náhled",
  name: "Název",
  text: "Text",
  // Profil průvodce (vize F6.4): stopa „Rozhovor 2. → Odpověď“, „Tipy → Co ochutnat“.
  countryIds: "Země",
  interview: "Rozhovor",
  favoritePlace: "Oblíbené místo nebo zážitek",
  food: "Co ochutnat",
  advice: "Rada před cestou",
};

/** Convert camelCase to Title Case: "priceFrom" → "Price from" */
function camelToTitle(str: string): string {
  return str
    .replace(/Id$/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/** Get a human-readable field label from a camelCase field name. */
export function getFieldLabel(fieldName: string): string {
  const overrides = i18n.language === "cs" ? FIELD_LABEL_OVERRIDES_CS : FIELD_LABEL_OVERRIDES;
  return overrides[fieldName] || FIELD_LABEL_OVERRIDES[fieldName] || camelToTitle(fieldName);
}

/**
 * Render a dotted issue path as a human trail: `tips.0.items.2.answer` →
 * "Tipy 1. → Položky 3. → Odpověď". Array indices become 1-based ordinals glued to the
 * collection they index, so the redactor is pointed at one row instead of a whole tab.
 */
export function getFieldPathLabel(path: string): string {
  const parts = path.split(".");
  const trail: string[] = [];
  for (const part of parts) {
    if (/^\d+$/.test(part)) {
      // Index belongs to the collection named just before it.
      if (trail.length > 0) trail[trail.length - 1] += ` ${Number(part) + 1}.`;
      continue;
    }
    trail.push(getFieldLabel(part));
  }
  return trail.join(" → ") || getFieldLabel(path);
}

// Common Zod error patterns → validation.* dictionary keys ({{n}} = capture group).
const ZOD_MESSAGE_MAP = [
  [/^Required$/, "validation.required"],
  [/^String must contain at least 1 character/, "validation.required"],
  [/^Expected number, received (null|string)/, "validation.invalidNumber"],
  [/^Number must be greater than or equal to (\d+)/, "validation.min"],
  [/^Number must be less than or equal to (\d+)/, "validation.max"],
  [/^String must contain at most (\d+) character/, "validation.maxLength"],
  [/^String must contain at least (\d+) character/, "validation.minLength"],
  [/^Invalid enum value/, "validation.invalidOption"],
  [/^Invalid url/, "validation.invalidUrl"],
  [/^Invalid email/, "validation.invalidEmail"],
  [/^Expected .+, received .+/, "validation.invalidValue"],
] as const;

/**
 * Localized text for a field error. A registry code (vize F13.9 — the API and the shared
 * catalogue schemas send codes, not text) goes through the admin dictionary; anything else is
 * a raw Zod message, simplified by `ZOD_MESSAGE_MAP` as before.
 */
export function humanizeFieldMessage(zodMessage: string): string {
  if (isApiErrorCode(zodMessage)) return apiErrorText(zodMessage);
  for (const [pattern, key] of ZOD_MESSAGE_MAP) {
    const match = zodMessage.match(pattern);
    if (match) {
      return i18n.t(key, { n: match[1] || "" });
    }
  }
  return zodMessage;
}

/** Get a localized user-friendly summary message based on error type. */
export function getUserMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // Registry-coded error: the client already rendered the dictionary text with its params.
    if (error.isCoded) return error.message;
    if (error.isNetwork) return i18n.t("validation.network");
    if (error.isValidation) {
      // Only promise a list when one will actually be rendered. A whole-object `.refine()`
      // (stay window, child ages) fails with an EMPTY `fieldErrors`, and "opravte tato pole:"
      // followed by nothing reads like the page swallowed the reason.
      return error.fieldErrors && Object.keys(error.fieldErrors).length > 0
        ? i18n.t("validation.fixHighlighted")
        : i18n.t("validation.checkForm");
    }
    if (error.isAuth) return i18n.t("validation.sessionExpired");
    if (error.isForbidden) return i18n.t("validation.forbidden");
    if (error.isNotFound) return i18n.t("validation.notFound");
    if (error.isServer) return i18n.t("validation.server");
  }
  if (error instanceof Error) {
    return error.message || i18n.t("validation.unexpected");
  }
  return i18n.t("validation.unexpected");
}
